import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import logger from '../utils/logger';
import { queryClient } from '../lib/queryClient';

const AuthContext = createContext();

// Platform super-admins — full access to the entire /internal portal. Defaults
// to the production identity; on dev set REACT_APP_SUPER_ADMIN_EMAILS
// (comma-separated). Keep this in sync with the server's SUPER_ADMIN_EMAILS.
const SUPER_ADMIN_EMAILS = (process.env.REACT_APP_SUPER_ADMIN_EMAILS || 'admin@indaratech.net')
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

// Blog managers — may enter /internal but see ONLY the Blogs tab (no usage,
// billing, org, or config access). Super-admins are implicitly blog managers
// too. Override via REACT_APP_BLOG_ADMIN_EMAILS. Keep in sync with the server's
// BLOG_ADMIN_EMAILS.
const BLOG_ADMIN_EMAILS = (process.env.REACT_APP_BLOG_ADMIN_EMAILS || 'support@talentcrux.ai')
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

const isSuperAdminEmail = (email) =>
  !!email && SUPER_ADMIN_EMAILS.includes(email.toLowerCase());

const isBlogAdminEmail = (email) =>
  isSuperAdminEmail(email) || (!!email && BLOG_ADMIN_EMAILS.includes(email.toLowerCase()));

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ---- helpers for persisting both tokens ----
function saveTokens(accessToken, refreshToken) {
  if (accessToken) localStorage.setItem('token', accessToken);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
}
function clearTokens() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
}

/** Server returned JSON message, or a clear hint when there is no HTTP response (e.g. API down). */
function getLoginErrorMessage(error, fallback = 'Login failed') {
  const serverMessage = error.response?.data?.message;
  if (serverMessage) return serverMessage;
  if (!error.response) {
    const code = error.code;
    if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
      return 'Unable to reach the server. Check your connection or try again in a moment.';
    }
    if (error.request) {
      return 'Unable to reach the server. Check your connection or try again in a moment.';
    }
  }
  return fallback;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Ref so interceptors always see current token without re-registering
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // Refresh-in-flight lock to prevent parallel refresh calls
  const refreshPromiseRef = useRef(null);

  const performLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    clearTokens();
    delete axios.defaults.headers.common['Authorization'];
    queryClient.clear();
  }, []);

  // Attempt to refresh the access token using the stored refresh token.
  // Returns the new access token on success, or null on failure.
  const attemptRefresh = useCallback(async () => {
    // If a refresh is already in-flight, piggyback on it
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      performLogout();
      return null;
    }

    const promise = (async () => {
      try {
        const { data } = await axios.post('/api/auth/refresh', {
          refreshToken: storedRefreshToken,
        }, {
          _skipAuthRefresh: true, // flag so the response interceptor doesn't recurse
        });
        const newAccess = data.token;
        const newRefresh = data.refreshToken;
        saveTokens(newAccess, newRefresh);
        setToken(newAccess);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
        return newAccess;
      } catch {
        performLogout();
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = promise;
    return promise;
  }, [performLogout]);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      logger.log('🔐 Axios headers set with token:', token.substring(0, 20) + '...');
    } else {
      delete axios.defaults.headers.common['Authorization'];
      logger.log('🔐 Axios headers cleared');
    }
  }, [token]);

  // Request interceptor: attach latest token
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const t = tokenRef.current;
        if (t) {
          config.headers.Authorization = `Bearer ${t}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    return () => axios.interceptors.request.eject(requestInterceptor);
  }, []);

  // Response interceptor: auto-refresh on 401
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Only attempt refresh if:
        // - 401 response
        // - Not already a refresh request (prevent infinite loop)
        // - Not already retried
        if (
          error.response?.status === 401 &&
          !originalRequest._skipAuthRefresh &&
          !originalRequest._retried
        ) {
          originalRequest._retried = true;
          const newToken = await attemptRefresh();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(responseInterceptor);
  }, [attemptRefresh]);

  // Check if user is authenticated on app load or when token changes
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          logger.log(
            '🔐 Auth check: Making request to /api/auth/me with token:',
            token.substring(0, 20) + '...'
          );
          const response = await axios.get('/api/auth/me');
          logger.log('🔐 Auth check successful:', response.data);
          if (response.data?.cached) {
            // eslint-disable-next-line no-console
            console.log('Auth /me loaded from token snapshot (cached=true)');
          }
          setUser(response.data);
        } catch (error) {
          console.error('❌ Auth check failed:', error);
          // The 401 interceptor will have already tried to refresh.
          // If we still got here, tokens are truly invalid.
          performLogout();
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, [token, performLogout]);

  // Sync auth across tabs
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== 'token') return;
      if (event.newValue) {
        setToken(event.newValue);
        toast.success('Signed in from another tab.');
      } else {
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
        toast.success('Signed out from another tab.');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { otpRequired, email: responseEmail, token: newToken, refreshToken: newRefresh, user: userData } = response.data;

      if (otpRequired && responseEmail) {
        return { success: false, otpRequired: true, email: responseEmail };
      }

      logger.log('🔐 Login response received:', {
        token: newToken ? 'present' : 'missing',
        user: userData?.email,
      });

      setToken(newToken);
      setUser(userData);
      saveTokens(newToken, newRefresh);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      logger.log('🔐 Login successful, token set:', newToken ? 'present' : 'missing');
      toast.success('Login successful!');
      const isSuperAdmin = isSuperAdminEmail(userData?.email);
      return { success: true, isPlatformSuperAdmin: isSuperAdmin };
    } catch (error) {
      const message = getLoginErrorMessage(error);
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verifyLoginOtp = async (email, otp) => {
    try {
      const response = await axios.post('/api/auth/verify-login-otp', { email, otp });
      const { token: newToken, refreshToken: newRefresh, user: userData } = response.data;

      setToken(newToken);
      setUser(userData);
      saveTokens(newToken, newRefresh);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Invalid or expired OTP';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resendLoginOtp = async (email) => {
    try {
      const response = await axios.post('/api/auth/resend-login-otp', { email });
      const message = response.data?.message || 'OTP sent to your email';
      toast.success(message);
      return { success: true, message };
    } catch (error) {
      const message = error.response?.data?.message || 'Unable to resend OTP';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const completeRegistration = (newToken, userData, newRefresh) => {
    setToken(newToken);
    setUser(userData);
    saveTokens(newToken, newRefresh);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    toast.success('Registration complete. You are now signed in.');
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      const { token: newToken, refreshToken: newRefresh, user: userInfo } = response.data;

      logger.log('🔐 Registration response received:', {
        token: newToken ? 'present' : 'missing',
        user: userInfo?.email,
      });

      setToken(newToken);
      setUser(userInfo);
      saveTokens(newToken, newRefresh);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      logger.log('🔐 Registration successful, token set:', newToken ? 'present' : 'missing');
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = (onLogoutComplete) => {
    performLogout();

    if (onLogoutComplete && typeof onLogoutComplete === 'function') {
      onLogoutComplete();
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/auth/profile', profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      if (response.data.token) {
        setToken(response.data.token);
        saveTokens(response.data.token, response.data.refreshToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }
      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isPlatformSuperAdmin: isSuperAdminEmail(user?.email),
    isBlogAdmin: isBlogAdminEmail(user?.email),
    login,
    verifyLoginOtp,
    resendLoginOtp,
    completeRegistration,
    register,
    logout,
    updateProfile,
    changePassword,
  };

  logger.log('🔐 AuthContext state:', {
    user: user ? { id: user._id || user.id, email: user.email } : null,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  });

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
